import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { getAuth } from 'firebase/auth';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import {
  subscribeExpiringLicenseAlerts,
  dismissExpiringLicenseAlert,
  getNotificationPreferences,
  ExpiringLicenseAlert,
} from '../../services/notification.service';

interface ExpiringLicensesModalProps {
  firebase: any;
}

const formatLicenseType = (type: string) => {
  switch (type) {
    case 'blanket': return 'Blanket License';
    case '1year':   return '1 Year';
    case '2year':   return '2 Years';
    case '3year':   return '3 Years';
    case 'custom':  return 'Custom Duration';
    default:        return type;
  }
};

const ExpiringLicensesModal: React.FC<ExpiringLicensesModalProps> = ({ firebase }) => {
  const [alerts, setAlerts] = useState<ExpiringLicenseAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());
  const [dismissingAll, setDismissingAll] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  const isOpen = useNotificationsStore((s) => s.showExpiringSoonModal);
  const setIsOpen = useNotificationsStore((s) => s.setShowExpiringSoonModal);
  const setExpiringSoonCount = useNotificationsStore((s) => s.setExpiringSoonCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Live subscription to pre-expiry alerts, gated on the user's in-app pref.
  useEffect(() => {
    if (!currentOrgId || !firebase) {
      setAlerts([]);
      setExpiringSoonCount(0);
      return;
    }

    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setLoading(true);
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const prefs = await getNotificationPreferences(firebase, currentOrgId, userId);
      if (cancelled) return;
      if (!prefs.inApp.expiringSoonLicenses) {
        setAlerts([]);
        setExpiringSoonCount(0);
        setLoading(false);
        return;
      }

      let autoOpenedThisOrg = false;
      unsubscribe = subscribeExpiringLicenseAlerts(firebase, currentOrgId, (incoming) => {
        setAlerts(incoming);
        setExpiringSoonCount(incoming.length);
        setLoading(false);

        if (!autoOpenedThisOrg && !hasAutoOpened && incoming.length > 0) {
          autoOpenedThisOrg = true;
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [currentOrgId, firebase, hasAutoOpened, setExpiringSoonCount, setIsOpen]);

  const handleDismiss = async (alert: ExpiringLicenseAlert) => {
    if (!currentOrgId || !firebase) return;
    setDismissingIds((prev) => new Set(prev).add(alert.id));
    try {
      await dismissExpiringLicenseAlert(firebase, {
        orgId: currentOrgId,
        alertId: alert.id,
      });
    } catch (err) {
      console.error('Error dismissing expiring alert:', err);
    } finally {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(alert.id);
        return next;
      });
    }
  };

  const handleDismissAll = async () => {
    if (!currentOrgId || !firebase) return;
    setDismissingAll(true);
    try {
      await Promise.all(
        alerts.map((a) =>
          dismissExpiringLicenseAlert(firebase, { orgId: currentOrgId, alertId: a.id }),
        ),
      );
    } catch (err) {
      console.error('Error dismissing all expiring alerts:', err);
    } finally {
      setDismissingAll(false);
    }
  };

  const handleRemindLater = () => setIsOpen(false);

  // Auto-close once all alerts cleared
  useEffect(() => {
    if (isOpen && alerts.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [alerts.length, isOpen, loading, setIsOpen]);

  if (!isOpen || alerts.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleRemindLater}
      style={{ color: '#ffffff', maxWidth: '600px', margin: '1.75rem auto' }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleRemindLater}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleRemindLater}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          Licenses Expiring Soon ({alerts.length})
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="light" />
          </div>
        ) : (
          <>
            <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
              These licenses are approaching their expiry date. You can renew or take action from each track's license history.
            </p>
            <div className="expiring-licenses-list">
              {alerts.map((alert) => {
                const daysLeft = Math.max(
                  0,
                  Math.ceil((alert.dateExpires - Date.now()) / (1000 * 60 * 60 * 24)),
                );
                const roleLabel = alert.recipientRole === 'artist'
                  ? `Issued to ${alert.otherPartyOrgName || 'a client'}`
                  : `From ${alert.otherPartyOrgName || 'an artist'}`;
                return (
                  <div
                    key={alert.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      marginBottom: '0.5rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          marginBottom: '0.25rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {alert.trackName || 'Unknown Track'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                        {roleLabel} | {formatLicenseType(alert.licenseType)} | Expires{' '}
                        {moment(alert.dateExpires).format('DD/MM/YYYY')} ({daysLeft} day{daysLeft === 1 ? '' : 's'} left)
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="btn-secondary-custom ml-3"
                      onClick={() => handleDismiss(alert)}
                      disabled={dismissingIds.has(alert.id) || dismissingAll}
                      style={{ minWidth: '80px' }}
                    >
                      {dismissingIds.has(alert.id) ? <Spinner size="sm" /> : 'Dismiss'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Button className="btn-secondary-custom" onClick={handleRemindLater} disabled={dismissingAll}>
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
        <Button
          className="btn-primary-custom"
          onClick={handleDismissAll}
          disabled={dismissingAll || alerts.length === 0}
        >
          {dismissingAll ? (
            <>
              <Spinner size="sm" className="mr-2" /> Dismissing...
            </>
          ) : (
            <span style={{ position: 'relative', zIndex: 2 }}>Dismiss All ({alerts.length})</span>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExpiringLicensesModal;
